import { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useForm, Controller, FormProvider } from 'react-hook-form';
import { Button, DialogTitle, Divider, Stack } from '@mui/material';

import useCooperativeId from 'shared/hooks/useCooperativeId';
import useDispatch from 'shared/hooks/useDispatch';
import useSelector from 'shared/hooks/useSelector';
import useTranslation from 'shared/hooks/useTranslation';
import { useUpdateCaseNotifications } from 'shared/hooks/useUpdateCaseNotifications';

import { CaseLevel1Code, CaseLevel2Code } from 'shared/constants/common-cases';

import getSelectHelpers from 'shared/utils/getSelectHelpers';
import getCaseLevels2BasedOnCaseLevel1 from 'shared/utils/common-cases/getCaseLevels2BasedOnCaseLevel1';
import getCaseLevels3BasedOnCaseLevel2 from 'shared/utils/common-cases/getCaseLevels3BasedOnCaseLevel2';

import { Dropzone } from 'features/file-loader';

import { userCommonCasesApi } from '../../api';
import { EMPTY_KEY } from '../../constants';
import {
  selectDefaultCaseLevel1,
  selectDefaultCaseLevel2,
} from '../../selectors';
import { resetCaseLevels } from '../../slice';

import type { FormState } from './UserCommonCaseCreateForm.types';

import PageLayout from 'layouts/PageLayout';
import Checkbox from 'shared/components/Checkbox';
import ControlLabel from 'shared/components/ControlLabel';
import Dialog from 'shared/components/Dialog';
import LoadingButton from 'shared/components/LoadingButton';
import ResidentNotification from './ResidentNotification';
import Select from 'shared/components/Select';
import SuccessfullyCreatedCaseDialog from 'shared/components/SuccessfullyCreatedCaseDialog';
import UnsavedChangesDialog from 'shared/components/UnsavedChangesDialog';
import ValidationWarning from 'components/validation-warning';

import {
  StyledTextarea,
  CheckboxContainer,
  Label,
} from './UserCommonCaseCreateForm.style';
import Instructions from './Instructions';

enum ValidationMessage {
  Required = 'Lisää puuttuva tieto',
}

type UserCommonCaseCreateFormProps = {
  onClose: () => void;
};

const UserCommonCaseCreateForm = ({
  onClose: onExternalClose,
}: UserCommonCaseCreateFormProps) => {
  const { t } = useTranslation();

  const coopId = useCooperativeId();
  const dispatch = useDispatch();
  const history = useHistory();

  const defaultCaseLevel1 = useSelector(selectDefaultCaseLevel1);
  const defaultCaseLevel2 = useSelector(selectDefaultCaseLevel2);

  const [showedFormDialog, showFormDialog] = useState(true);
  const [showedUnsavedChangesDialog, showUnsavedChangesDialog] =
    useState(false);
  const [showedSuccessDialog, showSuccessDialog] = useState(false);
  const updateCaseNotifications = useUpdateCaseNotifications();

  const [getCreateFormOptions, { data, isLoading, isFetching, isError }] =
    userCommonCasesApi.useLazyGetCommonCaseCreateFormOptionsQuery();
  const [
    createUserCommonCase,
    { data: newUserCommonCase, isLoading: isCreatingCase, isSuccess },
  ] = userCommonCasesApi.useCreateUserCommonCaseMutation();

  const formMethods = useForm<FormState>({
    criteriaMode: 'firstError',
    defaultValues: {
      caseLevel1: defaultCaseLevel1,
      caseLevel2: defaultCaseLevel2,
      caseLevel3: null,
      description: '',
      files: [],
      isAppartmentHasAdditionalLock: false,
      isMasterKeyAllowed: false,
      isRepairTimeNeedToBeAgreed: false,
      sendEmail: true,
      sendSms: false,
      spaceId: null,
    },
    reValidateMode: 'onSubmit',
  });
  const {
    control,
    clearErrors,
    formState: { errors, isDirty },
    register,
    watch,
    setValue,
    handleSubmit,
    unregister,
  } = formMethods;

  const spaceId = watch('spaceId');
  const caseLevel1 = watch('caseLevel1');
  const caseLevel2 = watch('caseLevel2');
  const hasOwnSpace = !!spaceId && spaceId !== EMPTY_KEY;

  const availableCaseLevels1 = useMemo(
    () => data?.CaseLevels1 ?? [],
    [data?.CaseLevels1]
  );
  const availableCaseLevels2 = useMemo(
    () =>
      data?.CaseLevels2 && caseLevel1
        ? getCaseLevels2BasedOnCaseLevel1(caseLevel1, data.CaseLevels2)
        : [],
    [caseLevel1, data?.CaseLevels2]
  );
  const availableCaseLevels3 = useMemo(
    () =>
      data?.CaseLevels3 && caseLevel2
        ? getCaseLevels3BasedOnCaseLevel2(caseLevel2, data.CaseLevels3)
        : [],
    [caseLevel2, data?.CaseLevels3]
  );

  const spacesSelectHelpers = useMemo(
    () => getSelectHelpers(data?.Spaces ?? []),
    [data?.Spaces]
  );
  const caseLevel1SelectHelpers = useMemo(
    () => getSelectHelpers(availableCaseLevels1),
    [availableCaseLevels1]
  );
  const caseLevel2SelectHelper = useMemo(
    () => getSelectHelpers(availableCaseLevels2),
    [availableCaseLevels2]
  );
  const caseLevel3SelectHelper = useMemo(
    () => getSelectHelpers(availableCaseLevels3),
    [availableCaseLevels3]
  );

  const submitForm = (data: FormState) => {
    if (!data.caseLevel1 || !data.caseLevel2 || !coopId) {
      console.warn(`Required fields is missing`, [
        `CaseLevel1 - ${data.caseLevel1}`,
        `CaseLevel2 - ${data.caseLevel2}`,
        `CooperativeId - ${coopId}`,
      ]);
      return;
    }

    createUserCommonCase({
      body: {
        CaseLevel1: data.caseLevel1,
        CaseLevel2: data.caseLevel2,
        CaseLevel3: data.caseLevel3 ?? null,
        CooperativeId: coopId,
        Description: data.description,
        HasDocuments: !!data.files.length,
        IsAppartmentHasAdditionalLock:
          data.isAppartmentHasAdditionalLock ?? false,
        IsMasterKeyAllowed: data.isMasterKeyAllowed ?? false,
        IsRepairTimeNeedToBeAgreed: data.isRepairTimeNeedToBeAgreed ?? false,
        SendEmail: data.sendEmail,
        SendSms: data.sendSms,
        SpaceId: data.spaceId === EMPTY_KEY ? null : data.spaceId,
      },
      files: data.files,
    });
  };

  const handleCloseDialog = () => {
    if (isDirty) {
      showUnsavedChangesDialog(true);
    } else {
      showFormDialog(false);
    }
  };

  const handleCloseFormWithoutSaving = () => {
    showUnsavedChangesDialog(false);
    showFormDialog(false);
  };

  const handleCloseUnsavedChangesDialog = () => {
    showUnsavedChangesDialog(false);
  };

  const handleCloseSuccessDialog = () => {
    showSuccessDialog(false);
    showFormDialog(false);
  };

  const handleCloseSuccessDialogWithRedirect = () => {
    showSuccessDialog(false);
    showFormDialog(false);

    if (isSuccess && newUserCommonCase) {
      history.push(`/incident/${newUserCommonCase.Id}`);
    }
  };

  const showCaseLevel3 = !!availableCaseLevels3.length;
  const showResidentNotification =
    caseLevel1 === CaseLevel1Code.Notification &&
    caseLevel2 === CaseLevel2Code.ResidentChanges;
  const showInstructionSection =
    hasOwnSpace && caseLevel1 === CaseLevel1Code.FlawNotification;

  useEffect(() => {
    if (coopId) {
      getCreateFormOptions(coopId);
    }
  }, [coopId]);

  useEffect(() => {
    if (!showInstructionSection) {
      unregister([
        'isAppartmentHasAdditionalLock',
        'isMasterKeyAllowed',
        'isRepairTimeNeedToBeAgreed',
      ]);
    }
  }, [showInstructionSection, unregister]);

  useEffect(() => {
    if (!showCaseLevel3) {
      unregister('caseLevel3');
    }
  }, [showCaseLevel3, unregister]);

  useEffect(() => {
    if (isSuccess) {
      showSuccessDialog(true);
      updateCaseNotifications();
    }
  }, [isSuccess]);

  useEffect(
    () => () => {
      dispatch(resetCaseLevels());
    },
    []
  );

  return (
    <Dialog
      open={showedFormDialog}
      fullWidth
      maxWidth="lg"
      onClose={handleCloseDialog}
      TransitionProps={{
        onExited: onExternalClose,
      }}
    >
      <PageLayout isLoading={isLoading || isFetching} isError={isError}>
        <UnsavedChangesDialog
          open={showedUnsavedChangesDialog}
          onApply={handleCloseFormWithoutSaving}
          onReject={handleCloseUnsavedChangesDialog}
        />
        {newUserCommonCase && (
          <SuccessfullyCreatedCaseDialog
            description={`${t('#portal.incident.label.title')} #${
              newUserCommonCase.Title
            }`}
            title={t('ServiceRequestSent')}
            open={showedSuccessDialog}
            onApply={handleCloseSuccessDialogWithRedirect}
            onClose={handleCloseSuccessDialog}
          />
        )}
        <FormProvider {...formMethods}>
          <Stack
            component="form"
            spacing={2.5}
            onSubmit={handleSubmit(submitForm)}
          >
            <DialogTitle>Palvelupyyntö</DialogTitle>
            <Stack
              direction={{
                xs: 'column',
                md: 'row',
              }}
              divider={<Divider orientation="vertical" flexItem />}
              spacing={2.5}
            >
              <Stack flex={1} spacing={2.5}>
                <Stack spacing={1}>
                  <ControlLabel required>
                    {t('##attribute#incident#uds_spaceid')}
                  </ControlLabel>
                  <ValidationWarning
                    text={errors.spaceId?.message}
                    handleInitErrors={() => clearErrors('spaceId')}
                    Component={
                      <Controller
                        control={control}
                        name="spaceId"
                        rules={{
                          required: ValidationMessage.Required,
                        }}
                        render={({ field: { value, onChange } }) => (
                          <Select
                            options={spacesSelectHelpers.options}
                            value={value ?? undefined}
                            variant="underline"
                            getOptionLabel={spacesSelectHelpers.getOptionLabel}
                            onChange={onChange}
                          />
                        )}
                      />
                    }
                  />
                </Stack>
                <Stack spacing={1}>
                  <ControlLabel required>
                    {t('##attribute#incident#uds_requesttype')}
                  </ControlLabel>
                  <ValidationWarning
                    text={errors.caseLevel1?.message}
                    handleInitErrors={() => clearErrors('caseLevel1')}
                    Component={
                      <Controller
                        control={control}
                        name="caseLevel1"
                        rules={{ required: ValidationMessage.Required }}
                        render={({ field: { value, onChange } }) => (
                          <Select
                            options={caseLevel1SelectHelpers.options}
                            value={value ?? undefined}
                            variant="underline"
                            getOptionLabel={
                              caseLevel1SelectHelpers.getOptionLabel
                            }
                            onChange={(...args) => {
                              onChange(...args);
                              setValue('caseLevel2', null);
                              setValue('caseLevel3', null);
                            }}
                          />
                        )}
                      />
                    }
                  />
                </Stack>
                <Stack spacing={1}>
                  <ValidationWarning
                    text={errors.caseLevel2?.message}
                    handleInitErrors={() => clearErrors('caseLevel2')}
                    Component={
                      <Controller
                        control={control}
                        name="caseLevel2"
                        rules={{ required: ValidationMessage.Required }}
                        render={({ field: { value, onChange } }) => (
                          <Select
                            options={caseLevel2SelectHelper.options}
                            value={value ?? undefined}
                            variant="underline"
                            getOptionLabel={
                              caseLevel2SelectHelper.getOptionLabel
                            }
                            onChange={(...args) => {
                              onChange(...args);
                              setValue('caseLevel3', null);
                            }}
                          />
                        )}
                      />
                    }
                  />
                </Stack>
                {showCaseLevel3 && (
                  <Stack spacing={1}>
                    <ValidationWarning
                      text={errors.caseLevel3?.message}
                      handleInitErrors={() => clearErrors('caseLevel3')}
                      Component={
                        <Controller
                          control={control}
                          name="caseLevel3"
                          rules={{ required: ValidationMessage.Required }}
                          render={({ field: { value, onChange } }) => (
                            <Select
                              options={caseLevel3SelectHelper.options}
                              value={value ?? undefined}
                              variant="underline"
                              getOptionLabel={
                                caseLevel3SelectHelper.getOptionLabel
                              }
                              onChange={onChange}
                            />
                          )}
                        />
                      }
                    />
                  </Stack>
                )}
                <Stack spacing={1}>
                  <ControlLabel required>
                    {t('#portal.incident.label.uds_additionalnotes')}
                  </ControlLabel>
                  <ValidationWarning
                    text={errors.description?.message}
                    handleInitErrors={() => clearErrors('description')}
                    Component={
                      <StyledTextarea
                        {...register('description', {
                          required: ValidationMessage.Required,
                        })}
                      />
                    }
                  />
                </Stack>
              </Stack>
              <Stack flex={1} spacing={3}>
                {showInstructionSection && <Instructions />}

                <Stack spacing={2}>
                  <Label>Seuranta</Label>
                  <Stack spacing={3}>
                    <CheckboxContainer>
                      <Controller
                        control={control}
                        name="sendSms"
                        render={({ field: { value, onChange } }) => (
                          <Checkbox checked={value} onChange={onChange} />
                        )}
                      />
                      <Label>{t('##attribute#incident#uds_needsendsms')}</Label>
                    </CheckboxContainer>
                    <CheckboxContainer>
                      <Controller
                        control={control}
                        name="sendEmail"
                        render={({ field: { value, onChange } }) => (
                          <Checkbox checked={value} onChange={onChange} />
                        )}
                      />
                      <Label>
                        {t('##attribute#incident#uds_needsendemail')}
                      </Label>
                    </CheckboxContainer>
                  </Stack>
                </Stack>

                <Stack spacing={2}>
                  <Label>{t('G_OtherInformation')}</Label>
                  <Controller
                    control={control}
                    name="files"
                    render={({ field: { value, onChange } }) => (
                      <Dropzone value={value} onChange={onChange} />
                    )}
                  />
                </Stack>

                {showResidentNotification && <ResidentNotification />}
              </Stack>
            </Stack>
            <Stack direction="row" spacing={2} justifyContent="flex-end">
              <Button color="neutral" onClick={handleCloseDialog}>
                {t('#portal.common.button.cancel')}
              </Button>
              <LoadingButton
                color="success"
                disabled={isCreatingCase}
                loading={isCreatingCase}
                type="submit"
              >
                {t('Send')}
              </LoadingButton>
            </Stack>
          </Stack>
        </FormProvider>
      </PageLayout>
    </Dialog>
  );
};

export default UserCommonCaseCreateForm;
